<template>
  <div class="min-h-screen flex flex-col bg-white text-fg font-sans">
    <!-- Header -->
    <header
      data-testid="header"
      class="fixed top-0 left-0 right-0 z-40 pt-safe backdrop-blur-xl backdrop-saturate-150 border-b transition-all duration-300 bg-white/85 border-line/60 shadow-sm"
    >
      <div class="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
        <router-link
          data-testid="link-logo"
          class="flex items-center gap-2 cursor-pointer"
          to="/"
        >
          <img src="/images/tble_logo2.svg" alt="티블비즈" class="h-7 w-auto" />
        </router-link>

        <nav class="hidden md:flex items-center gap-6">
          <button
            data-testid="nav-서비스"
            class="text-sm font-medium transition-colors cursor-pointer text-fg-muted hover:text-fg"
            @click="navToHomeSection('services')"
          >
            서비스
          </button>
          <button
            data-testid="nav-혜택"
            class="text-sm font-medium transition-colors cursor-pointer text-fg-muted hover:text-fg"
            @click="navToHomeSection('benefits')"
          >
            혜택
          </button>
          <button
            data-testid="nav-성과"
            class="text-sm font-medium transition-colors cursor-pointer text-fg-muted hover:text-fg"
            @click="navToHomeSection('performance')"
          >
            성과
          </button>
          <button
            data-testid="nav-진행 방식"
            class="text-sm font-medium transition-colors cursor-pointer text-fg-muted hover:text-fg"
            @click="navToHomeSection('process')"
          >
            진행 방식
          </button>
          <button
            data-testid="nav-FAQ"
            class="text-sm font-medium transition-colors cursor-pointer text-fg-muted hover:text-fg"
            @click="navToHomeSection('faq')"
          >
            FAQ
          </button>
        </nav>

        <div class="flex items-center gap-3">
          <button
            data-testid="button-header-cta"
            class="hidden md:inline-flex items-center px-5 py-2.5 rounded-[12px] border text-sm font-semibold backdrop-blur-sm transition-colors cursor-pointer border-line bg-white/60 text-fg hover:bg-white"
            @click="openModal"
          >
            무료 상담 신청
          </button>
          <button
            data-testid="button-mobile-menu"
            class="md:hidden -mr-2 flex items-center justify-center min-w-[44px] min-h-[44px] transition-colors cursor-pointer text-fg"
            aria-label="메뉴 열기"
            @click="isMobileMenuOpen = !isMobileMenuOpen"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              class="lucide lucide-menu"
              aria-hidden="true"
            >
              <path d="M4 5h16" />
              <path d="M4 12h16" />
              <path d="M4 19h16" />
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile Nav Drawer -->
      <div v-if="isMobileMenuOpen" class="md:hidden bg-white border-b border-line px-4 py-4 space-y-3 shadow-md">
        <button class="block w-full text-left py-2 text-sm font-medium text-fg-muted hover:text-fg" @click="navToHomeSection('services')">서비스</button>
        <button class="block w-full text-left py-2 text-sm font-medium text-fg-muted hover:text-fg" @click="navToHomeSection('benefits')">혜택</button>
        <button class="block w-full text-left py-2 text-sm font-medium text-fg-muted hover:text-fg" @click="navToHomeSection('performance')">성과</button>
        <button class="block w-full text-left py-2 text-sm font-medium text-fg-muted hover:text-fg" @click="navToHomeSection('process')">진행 방식</button>
        <button class="block w-full text-left py-2 text-sm font-medium text-fg-muted hover:text-fg" @click="navToHomeSection('faq')">FAQ</button>
        <button data-testid="button-header-cta" class="w-full py-3 rounded-xl bg-brand-500 text-white font-bold text-sm" @click="openModal">무료 상담 신청</button>
      </div>
    </header>

    <!-- Main Content -->
    <main class="flex-1">
      <div class="pt-16 pb-24 md:pb-0">
        <!-- Hero Section -->
        <section class="py-16 md:py-24 bg-gradient-to-br from-brand-50 to-white">
          <div class="max-w-7xl mx-auto px-4 md:px-6">
            <span class="text-xs font-bold uppercase tracking-widest text-brand-500 mb-3 block">CASES</span>
            <h1 class="text-2xl md:text-4xl font-bold text-fg mb-5">업종별 성과 사례</h1>
            <p class="text-base text-fg-muted leading-relaxed max-w-xl">광고주 대시보드에서 확인된 실제 운영 결과를 공유합니다.</p>
          </div>
        </section>

        <!-- Cases Filter & Grid Section -->
        <section class="py-16 bg-white">
          <div class="max-w-7xl mx-auto px-4 md:px-6">
            <!-- Filter Tabs -->
            <div class="flex flex-wrap gap-2 mb-8">
              <button
                v-for="tab in filterTabs"
                :key="tab"
                :data-testid="'filter-tab-' + tab"
                class="px-5 py-2.5 rounded-full text-sm font-semibold transition-colors cursor-pointer"
                :class="selectedTab === tab ? 'bg-brand-500 text-white' : 'bg-bg-soft border border-line text-fg-muted hover:border-brand-300'"
                @click="selectedTab = tab"
              >
                {{ tab }}
              </button>
            </div>

            <!-- Case Cards Grid -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
              <div
                v-for="(card, idx) in filteredCases"
                :key="idx"
                :data-testid="'case-card-' + idx"
                class="bg-bg-soft rounded-[16px] p-6 border border-line hover:shadow-md transition-shadow flex flex-col"
              >
                <div class="flex flex-wrap gap-2 mb-4">
                  <span class="px-2.5 py-1 rounded-full bg-brand-100 text-brand-700 text-xs font-semibold">
                    {{ card.category }}
                  </span>
                  <span class="px-2.5 py-1 rounded-full bg-white border border-line text-fg-muted text-xs font-medium">
                    {{ card.combo }}
                  </span>
                </div>
                <p class="text-base font-semibold text-fg mb-3 leading-snug">"{{ card.quote }}"</p>
                <div class="flex items-center gap-2 mt-auto pt-4">
                  <div class="w-1.5 h-1.5 rounded-full bg-emerald-500"></div>
                  <span class="text-sm text-emerald-600 font-medium">{{ card.result }}</span>
                </div>
              </div>
            </div>

            <p class="text-xs text-fg-subtle mb-8">수치는 특정 광고주의 운영 결과이며 업종·상품·키워드·기간에 따라 달라질 수 있습니다.</p>

            <button
              data-testid="button-cases-cta"
              class="px-8 py-4 rounded-[12px] bg-brand-500 text-white font-semibold text-base hover:bg-brand-600 transition-colors cursor-pointer"
              @click="openModal"
            >
              성과 상담받기
            </button>
          </div>
        </section>
      </div>
    </main>

    <!-- Footer -->
    <footer class="bg-fg text-white/70 pt-12 pb-28 md:pb-12">
      <div class="max-w-7xl mx-auto px-4 md:px-6">
        <div class="mb-8 flex items-center gap-3">
          <img src="/images/tble_logo2.svg" alt="티블비즈" class="h-7 w-auto" />
        </div>
        <p class="text-sm text-white/50 mb-8 -mt-4">다채널 리뷰 마케팅으로 매출을 올리는 광고주 서비스</p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs leading-relaxed mb-8 text-white/50">
          <div class="space-y-1">
            <p>상호명: 주식회사 쓰리얼라이언스</p>
            <p>대표이사: 성동효</p>
            <p>주소: 서울시 강남구 학동로 44길 16, 1F</p>
            <p>사업자등록번호: 217-81-47577</p>
            <p>통신판매등록번호: 제2025-서울강남-04400호</p>
          </div>
          <div class="space-y-1">
            <p>개인정보 보호 최고책임자: 성동효</p>
            <p>이메일: help@3alliance.co.kr</p>
            <!-- <p>대표전화: 1833-5985</p> -->
            <!-- <p>FAX: 0504-334-6358</p> -->
            <p>상담시간: 10:00 ~ 18:00 · 토·일·공휴일 휴무</p>
          </div>
        </div>

        <div class="border-t border-white/10 pt-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-1 md:gap-4">
          <p class="text-xs text-white/40 py-2">© 2026 주식회사 쓰리얼라이언스. All rights reserved.</p>
          <div class="flex gap-4 -my-1">
            <a href="#" data-testid="footer-link-개인정보 처리방침" class="inline-flex items-center min-h-[44px] text-xs text-white/50 hover:text-white transition-colors" @click.prevent>개인정보 처리방침</a>
            <a href="#" data-testid="footer-link-이용약관" class="inline-flex items-center min-h-[44px] text-xs text-white/50 hover:text-white transition-colors" @click.prevent>이용약관</a>
          </div>
        </div>
      </div>
    </footer>

    <!-- Floating Contact Button -->
    <button
      type="button"
      data-testid="button-floating-contact"
      aria-label="무료 상담 신청"
      class="fixed bottom-6 right-4 md:bottom-8 md:right-6 z-[9998] flex items-center gap-2 pl-4 pr-5 h-14 rounded-full bg-brand-500 text-white font-semibold text-sm shadow-[0_8px_24px_rgba(242,178,42,0.45)] hover:bg-brand-600 hover:shadow-[0_12px_28px_rgba(242,178,42,0.55)] transition-all hover:scale-105 cursor-pointer"
      @click="toggleModal"
    >
      <span class="flex items-center justify-center w-7 h-7 rounded-full bg-white/15">
        <img src="/images/headset.svg" alt="" class="w-4 h-4 brightness-0 invert" />
      </span>
      <span>문의하기</span>
    </button>

    <!-- Consultation Widget Modal -->
    <ConsultationModal :is-open="isModalOpen" @close="isModalOpen = false" />
  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import ConsultationModal from '../common/ConsultationModal.vue';

const router = useRouter();
const isMobileMenuOpen = ref(false);
const isModalOpen = ref(false);
const selectedTab = ref('전체');

const filterTabs = ['전체', '로컬 매장', '스마트 스토어', '브랜드·제품', '앱·서비스'];

const allCases = [
  { category: '로컬 매장', combo: '블로그 + 맵리뷰', quote: '플레이스 순위가 눈에 띄게 올라왔어요', result: '방문 증가' },
  { category: '스마트 스토어', combo: '구매평 + 블로그', quote: '광고비가 매출로 회수되기 시작했어요', result: '전환율 개선' },
  { category: '브랜드·제품', combo: '인스타 + 블로그', quote: '신상품 런칭 첫 주에 반응이 달랐어요', result: 'SNS 확산' },
  { category: '로컬 매장', combo: '맵리뷰 + 블로그', quote: '오픈 초기 리뷰 공백을 빠르게 채웠어요', result: '플레이스 신뢰' },
  { category: '스마트 스토어', combo: '구매평 + 인스타', quote: '경쟁사 대비 후기 격차가 줄었어요', result: '비교 우위' },
  { category: '앱·서비스', combo: '앱 리뷰 + 인스타', quote: '출시 직후 평점이 안정적으로 유지됐어요', result: '마켓 신뢰' },
];

const filteredCases = computed(() => {
  if (selectedTab.value === '전체') return allCases;
  return allCases.filter((item) => item.category === selectedTab.value);
});

const navToHomeSection = (sectionId) => {
  isMobileMenuOpen.value = false;
  router.push({ path: '/', hash: '#' + sectionId });
};

const openModal = () => {
  isModalOpen.value = true;
};

const toggleModal = () => {
  isModalOpen.value = !isModalOpen.value;
};
</script>
